import { useEffect, useState } from 'react';

import { cn } from '@/lib/cn';

interface MatchCountdownProps {
  /** Server-provided remaining seconds. Resets the local timer when this prop changes. */
  countdown: number;
  /** Fires once when the local timer hits 0. */
  onExpire?: () => void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

/**
 * Local countdown timer for autohandled-match states (CHECKIN, WAITING…).
 * Server resends the authoritative `countdown` every 2s via polling — when
 * the prop changes, we reset the local timer to stay in sync.
 *
 * Web port of mobile-app/src/components/commons/match-countdown.tsx.
 */
export function MatchCountdown({
  countdown,
  onExpire,
  size = 'medium',
  className,
}: MatchCountdownProps) {
  const [timeLeft, setTimeLeft] = useState(countdown);

  useEffect(() => {
    setTimeLeft(countdown);
  }, [countdown]);

  useEffect(() => {
    if (timeLeft <= 0) {
      onExpire?.();
      return;
    }
    const id = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          onExpire?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [timeLeft, onExpire]);

  const formatted = formatHMS(Math.max(timeLeft, 0));
  const fontSize =
    size === 'small' ? 'text-base' : size === 'large' ? 'text-4xl' : 'text-2xl';

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <span className={cn('font-bold tabular-nums text-fg', fontSize)}>
        {formatted}
      </span>
    </div>
  );
}

function formatHMS(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(secs)}`
    : `${pad(minutes)}:${pad(secs)}`;
}
